import { useEffect, useState } from 'react';
import { PostCommunicationContent } from 'app/modules/posts/modules/post/components/postCommunication/types/postCommunicationContent';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { Comment } from 'app/modules/comments/types';
import { useTransition, UseTransitionProps } from '@react-spring/web';
import { config } from 'react-spring';
import useDebounce from 'utils/hooks/useDebounce';
import usePrevious from 'utils/hooks/usePrevious';

const TRANSITION: UseTransitionProps = {
  from: { opacity: 0, transform: 'translateY(-100%)' },
  enter: { opacity: 1, transform: 'translateY(0%)' },
  leave: { opacity: 0, transform: 'translateY(-100%)' },
  config: {
    ...config.stiff,
    clamp: true,
  },
};

type Props = {
  commentsLength: number;
};

export const useStickyApprovalRequest = ({ commentsLength }: Props) => {
  const {
    activeContent,
    user: currentUser,
    readyToTrackVisibility,
  } = usePostCommunicationContext();
  const prevCommentsLength = usePrevious(commentsLength);
  const [isRequestVisibleInCommentsList, setIsRequestVisibleInCommentsList] =
    useState(true);

  const isStickyApprovalRequestVisibleForClient = useDebounce(
    PostCommunicationContent.CLIENT === activeContent &&
      currentUser.role === 'client' &&
      !isRequestVisibleInCommentsList,
  );

  const isStickyApprovalRequestVisibleForAgency = useDebounce(
    (PostCommunicationContent.CLIENT === activeContent ||
      PostCommunicationContent.INTERNAL === activeContent) &&
      currentUser.role !== 'client' &&
      !isRequestVisibleInCommentsList,
  );

  useEffect(() => {
    if (!prevCommentsLength) return;
    setIsRequestVisibleInCommentsList(true);
  }, [commentsLength, activeContent, prevCommentsLength]);

  const onCommentWorkflowsIsVisibleChange = (
    comment: Comment,
  ): ((isVisible: boolean) => void) | undefined => {
    if (
      ['client_approval_request', 'internal_approval_request'].includes(
        comment.type,
      ) &&
      !comment.meta &&
      !comment.deleted_at &&
      readyToTrackVisibility
    ) {
      return (isVisible: boolean) =>
        setIsRequestVisibleInCommentsList(isVisible);
    }
  };

  const clientTransition = useTransition(
    isStickyApprovalRequestVisibleForClient,
    TRANSITION,
  );

  const agencyTransition = useTransition(
    isStickyApprovalRequestVisibleForAgency,
    TRANSITION,
  );

  return {
    isStickyApprovalRequestVisibleForClient,
    isStickyApprovalRequestVisibleForAgency,
    clientTransition,
    agencyTransition,
    onCommentWorkflowsIsVisibleChange,
  };
};
